import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api, { apiError } from "@/lib/api";
import MapView from "@/components/MapView";
import { stopStyle, fmtDuration, ROUTE_STATUS_LABEL } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Sparkles, ShieldCheck, ShieldAlert, Lock, Unlock, GripVertical,
  TrendingDown, Loader2, CheckCircle2, Send, RotateCcw, Info, AlertTriangle, Waypoints, School, X,
} from "lucide-react";
import { toast } from "sonner";

const fmtClock = (min) => {
  if (min == null) return "—";
  const m = Math.round(min);
  return `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

function Metric({ label, value, sub }) {
  return (
    <div className="p-3">
      <div className="overline">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Analysis() {
  const { id } = useParams();
  const nav = useNavigate();
  const [route, setRoute] = useState(null);
  const [orig, setOrig] = useState(null);
  const [opt, setOpt] = useState(null);
  const [mode, setMode] = useState("original");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [safetyAlert, setSafetyAlert] = useState(null);   // recalcul sécurité après glisser-déposer
  const [prevStops, setPrevStops] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/routes/${id}`); setRoute(r.data);
    const [o, p] = await Promise.all([api.get(`/routes/${id}/original`), api.get(`/routes/${id}/optimized`)]);
    setOrig(o.data); setOpt(p.data);
  }, [id]);
  useEffect(() => { load().catch(() => toast.error("Chargement impossible")); }, [load]);

  const act = async (path, msg) => {
    setBusy(true);
    try { await api.post(`/routes/${id}/${path}`); toast.success(msg); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onDragEnd = async (res) => {
    const seqNow = (opt?.stops || []).length ? opt : orig;
    if (!res.destination || !seqNow) return;
    const target = seqNow === opt ? "optimized" : "original";
    const setSeq = seqNow === opt ? setOpt : setOrig;
    const before = seqNow.stops;
    const stops = Array.from(seqNow.stops);
    const [m] = stops.splice(res.source.index, 1);
    stops.splice(res.destination.index, 0, m);
    const beforeViolations = (seqNow.metrics || {}).violations || 0;
    setSeq({ ...seqNow, stops });
    setSafetyAlert({ pending: true });
    try {
      const locked = {}; stops.forEach((s) => (locked[s.id] = !!s.locked));
      const { data } = await api.post(`/routes/${id}/reorder`, { order: stops.map((s) => s.id), locked, target });
      setSeq((p) => ({ ...p, stops: data.stops, metrics: data.metrics }));
      setPrevStops(before);
      setSafetyAlert({
        moved: m.address,
        can_publish: data.can_publish,
        violations: data.metrics.violations || 0,
        delta: (data.metrics.violations || 0) - beforeViolations,
        unsafe_stops: data.unsafe_stops || [],
        distance_km: data.metrics.distance_km,
        duration_min: data.metrics.duration_min,
        school_late_min: data.metrics.school_late_min || 0,
      });
      load().catch(() => {});
    } catch (e) {
      setSafetyAlert(null);
      toast.error(apiError(e.response?.data?.detail)); load();
    }
  };

  const undoReorder = async () => {
    if (!prevStops) return;
    try {
      const locked = {}; prevStops.forEach((s) => (locked[s.id] = !!s.locked));
      await api.post(`/routes/${id}/reorder`, { order: prevStops.map((s) => s.id), locked,
        target: (opt?.stops || []).length ? "optimized" : "original" });
      setPrevStops(null); setSafetyAlert(null);
      await load();
      toast.success("Modification annulée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleLock = async (s) => {
    await api.put(`/routes/${id}/stops/${s.id}`, { locked: !s.locked });
    await load();
    toast.success(!s.locked ? "Arrêt verrouillé" : "Arrêt déverrouillé");
  };

  const validateStop = async (s) => {
    await api.put(`/routes/${id}/stops/${s.id}`, { safety_override: "VALIDATED" });
    toast.success("Sécurité validée manuellement"); setDetail(null); await load();
  };

  if (!route || !orig || !opt) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  const hasOpt = !!(opt.stops || []).length;
  const mo = orig.metrics || {};
  const mp = hasOpt ? (opt.metrics || {}) : mo;     // sans optimisation: on lit la séquence importée
  const seq = hasOpt ? opt : orig;                  // séquence de travail affichée dans la liste
  const current = mode === "original" || !hasOpt ? orig : opt;
  const gainKm = (mo.distance_km || 0) - (mp.distance_km || 0);
  const gainMin = (mo.duration_min || 0) - (mp.duration_min || 0);
  const pctKm = mo.distance_km ? ((gainKm / mo.distance_km) * 100).toFixed(1) : 0;
  const pctMin = mo.duration_min ? ((gainMin / mo.duration_min) * 100).toFixed(1) : 0;
  const score = mp.safety_score ?? 0;

  return (
    <div className="flex flex-col h-screen">
      <div className="sticky top-0 z-20 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav("/routes")} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="font-display text-xl font-bold">Route {route.route_number}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="secondary">{ROUTE_STATUS_LABEL[route.status] || route.status}</Badge>
              <span className="text-xs text-muted-foreground">{route.school}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-md text-sm font-bold flex items-center gap-1.5 ${mp.approvable ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="safety-score">
            {mp.approvable ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />} Sécurité {score}/100
          </div>
          <Button variant="outline" onClick={() => act("optimize", "Optimisation terminée")} disabled={busy} data-testid="optimize-btn"><Sparkles className="w-4 h-4 mr-1" /> Optimiser</Button>
          <Button variant="outline" onClick={() => act("approve", "Route approuvée")} disabled={busy || !mp.approvable} data-testid="approve-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Approuver</Button>
          <Button onClick={() => act("publish", "Route publiée")} disabled={busy || !mp.approvable} data-testid="publish-btn"><Send className="w-4 h-4 mr-1" /> Publier</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 flex-1 min-h-0">
        <div className="col-span-12 lg:col-span-7 relative border-r border-border">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[400]">
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="shadow-md">
                <TabsTrigger value="original" data-testid="mode-original">Originale</TabsTrigger>
                {hasOpt && <TabsTrigger value="optimized" data-testid="mode-optimized">Optimisée</TabsTrigger>}
                {hasOpt && <TabsTrigger value="compare" data-testid="mode-compare">Comparaison</TabsTrigger>}
              </TabsList>
            </Tabs>
          </div>
          <MapView
            stops={mode === "compare" ? opt.stops : current.stops}
            geometry={mode === "compare" ? opt.geometry : current.geometry}
            compareGeometry={mode === "compare" ? orig.geometry : null}
            polylineColor={mode === "original" ? "#64748B" : "#10B981"}
            compareColor="#64748B"
            garage={route.garage}
            school={route.school_point}
          />
        </div>

        <div className="col-span-12 lg:col-span-5 overflow-y-auto">
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 gap-px bg-border border border-border rounded-lg overflow-hidden">
              <div className="bg-card">
                <div className="px-3 pt-3 overline text-slate-500">Originale</div>
                <div className="grid grid-cols-2 divide-x divide-border">
                  <Metric label="Distance" value={`${mo.distance_km || 0} km`} />
                  <Metric label="Temps" value={fmtDuration(mo.duration_min)} />
                </div>
                <div className="grid grid-cols-3 divide-x divide-border border-t border-border">
                  <Metric label="Arrêts" value={mo.stops || 0} />
                  <Metric label="Traversées" value={mo.unsafe_crossings || 0} />
                  <Metric label="Demi-tours" value={mo.u_turns || 0} />
                </div>
              </div>
              <div className="bg-card">
                <div className="px-3 pt-3 overline text-emerald-600">{hasOpt ? "Optimisée" : "Optimisation non lancée"}</div>
                <div className="grid grid-cols-2 divide-x divide-border">
                  <Metric label="Distance" value={hasOpt ? `${mp.distance_km || 0} km` : "—"} />
                  <Metric label="Temps" value={hasOpt ? fmtDuration(mp.duration_min) : "—"} />
                </div>
                <div className="grid grid-cols-3 divide-x divide-border border-t border-border">
                  <Metric label="Arrêts" value={hasOpt ? (mp.stops || 0) : "—"} />
                  <Metric label="Traversées" value={hasOpt ? (mp.unsafe_crossings || 0) : "—"} />
                  <Metric label="Demi-tours" value={hasOpt ? (mp.u_turns || 0) : "—"} />
                </div>
              </div>
            </div>

            {hasOpt && (
            <div className="rounded-lg bg-primary text-primary-foreground p-5 flex items-center justify-between">
              <div className="flex items-center gap-2"><TrendingDown className="w-5 h-5 text-emerald-400" /><span className="font-display font-semibold">Économie</span></div>
              <div className="flex gap-6 text-right">
                <div><div className="font-display text-xl font-bold">-{gainKm.toFixed(1)} km</div><div className="text-xs opacity-70">-{pctKm}%</div></div>
                <div><div className="font-display text-xl font-bold">-{Math.round(gainMin)} min</div><div className="text-xs opacity-70">-{pctMin}%</div></div>
              </div>
            </div>
            )}

            <div className={`rounded-lg border p-4 flex items-center justify-between ${mp.school_late_min ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="school-eta">
              <div className="flex items-center gap-2">
                <School className={`w-5 h-5 ${mp.school_late_min ? "text-red-600" : "text-emerald-600"}`} />
                <div>
                  <div className="font-display font-semibold text-sm">Destination : {route.school || "école non définie"}</div>
                  <div className="text-xs text-muted-foreground">
                    {route.school_point?.lat ? "La route se termine à l'école" : "Adresse de l'école à géocoder"}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl font-bold">{fmtClock(mp.eta_school_min)}</div>
                <div className="text-xs text-muted-foreground">max {route.school_arrival_max || "—"}
                  {mp.school_late_min ? ` · retard ${mp.school_late_min} min` : " · à l'heure"}</div>
              </div>
            </div>

            {!!(route.explanations || []).length && (
            <div>
              <h3 className="font-display font-semibold flex items-center gap-2 mb-2"><Info className="w-4 h-4" /> Pourquoi cette route ?</h3>
              <ul className="space-y-2">
                {(route.explanations || []).map((e, i) => (
                  <li key={i} className="text-sm bg-secondary rounded-md px-3 py-2 border-l-2 border-emerald-500" data-testid={`explanation-${i}`}>{e}</li>
                ))}
              </ul>
            </div>
            )}

            <div>
              <h3 className="font-display font-semibold flex items-center gap-2 mb-2"><Waypoints className="w-4 h-4" /> {hasOpt ? "Séquence optimisée" : "Ordre des arrêts (importé)"} <span className="text-xs text-muted-foreground font-normal">(glisser pour réordonner)</span></h3>
              {safetyAlert && (
                <div className={`mb-3 rounded-lg border p-3 text-sm ${
                  safetyAlert.pending ? "bg-secondary border-border"
                    : safetyAlert.can_publish ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-300"
                }`} data-testid="reorder-safety-alert">
                  {safetyAlert.pending ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Recalcul de la sécurité…
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 font-semibold">
                          {safetyAlert.can_publish
                            ? <><ShieldCheck className="w-4 h-4 text-emerald-600" /> Modification sécuritaire — publiable</>
                            : <><ShieldAlert className="w-4 h-4 text-red-600" /> {safetyAlert.violations} traversée(s) dangereuse(s) — non publiable</>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {prevStops && (
                            <Button size="sm" variant="outline" onClick={undoReorder} data-testid="undo-reorder">
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Annuler
                            </Button>
                          )}
                          <button onClick={() => setSafetyAlert(null)} className="text-slate-400 hover:text-slate-700" data-testid="dismiss-safety-alert">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1.5 text-xs text-muted-foreground">
                        « {safetyAlert.moved} » déplacé · {safetyAlert.distance_km} km · {fmtDuration(safetyAlert.duration_min)}
                        {safetyAlert.delta > 0 && <span className="text-red-600 font-semibold"> · +{safetyAlert.delta} violation(s)</span>}
                        {safetyAlert.delta < 0 && <span className="text-emerald-700 font-semibold"> · {safetyAlert.delta} violation(s)</span>}
                        {safetyAlert.school_late_min > 0 && <span className="text-red-600 font-semibold"> · retard école {safetyAlert.school_late_min} min</span>}
                      </div>
                      {!!safetyAlert.unsafe_stops.length && (
                        <ul className="mt-2 space-y-1">
                          {safetyAlert.unsafe_stops.slice(0, 4).map((s) => (
                            <li key={s.id} className="text-xs flex items-start gap-1.5" data-testid={`unsafe-stop-${s.index}`}>
                              <AlertTriangle className={`w-3.5 h-3.5 mt-px shrink-0 ${s.status === "VIOLATION" ? "text-red-500" : "text-amber-500"}`} />
                              <span><b>#{s.index}</b> {s.address} — {s.status === "VIOLATION"
                                ? `arrêt du côté ${s.stop_side === "LEFT" ? "GAUCHE" : s.stop_side} sur ${s.road_name || "cette route"} (côté droit obligatoire)`
                                : "validation manuelle requise (côté indéterminé)"}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="stops">
                  {(prov) => (
                    <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-1.5" data-testid="stop-list">
                      {seq.stops.map((s, i) => {
                        const st = stopStyle(s);
                        return (
                          <Draggable key={s.id} draggableId={s.id} index={i} isDragDisabled={s.locked}>
                            {(p, snap) => (
                              <div ref={p.innerRef} {...p.draggableProps}
                                className={`flex items-center gap-2 bg-card border rounded-md px-2 py-2 ${snap.isDragging ? "shadow-lg" : ""} ${s.safety_status === "VIOLATION" ? "border-red-300" : "border-border"}`}
                                onClick={() => setDetail(s)} data-testid={`stop-item-${i}`}>
                                <span {...p.dragHandleProps} className="text-slate-300 cursor-grab" onClick={(e) => e.stopPropagation()}>
                                  {s.locked ? <Lock className="w-4 h-4" /> : <GripVertical className="w-4 h-4" />}
                                </span>
                                <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: st.color }}>{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{s.address}</div>
                                  <div className="text-xs text-muted-foreground truncate">{s.pickup_time} · {s.city}</div>
                                </div>
                                {s.numbered_road && <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">Rte {s.road_number}</Badge>}
                                {s.safety_status === "VIOLATION" && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
                                {s.safety_status === "VALIDATION" && <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                                <button onClick={(e) => { e.stopPropagation(); toggleLock(s); }} className="text-slate-400 hover:text-primary" data-testid={`lock-${i}`}>
                                  {s.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                </button>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {prov.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <Button variant="outline" className="w-full mt-3" onClick={() => act("optimize", "Réoptimisée")} disabled={busy} data-testid="reoptimize-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> {hasOpt ? "Ré-optimiser automatiquement" : "Optimiser automatiquement (facultatif)"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent data-testid="stop-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display">Arrêt #{seq.stops.findIndex((x) => x.id === detail.id) + 1}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="font-semibold text-base">{detail.address}</div>
                <div className="text-muted-foreground">{detail.city}</div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Info2 k="Route" v={detail.road_number ? `Route ${detail.road_number}` : detail.road_name} />
                  <Info2 k="Limite" v={`${detail.speed_limit || "?"} km/h`} />
                  <Info2 k="Côté adresse" v={detail.building_side || "—"} />
                  <Info2 k="Direction bus" v={detail.heading_cardinal || "—"} />
                  <Info2 k="Position relative" v={detail.stop_side || "—"} />
                  <Info2 k="Côté requis" v={detail.required_stop_side || "—"} />
                </div>
                <div className={`mt-3 px-3 py-2 rounded-md font-semibold flex items-center gap-2 ${stopStyle(detail).text} bg-secondary`}>
                  {stopStyle(detail).label}
                </div>
                {detail.numbered_road && <div className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Route numérotée — ramassage côté droit obligatoire</div>}
                {detail.safety_status === "VALIDATION" && (
                  <Button className="w-full mt-2" onClick={() => validateStop(detail)} data-testid="validate-stop">Valider la sécurité manuellement</Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info2({ k, v }) {
  return <div className="bg-secondary rounded px-2 py-1.5"><div className="overline">{k}</div><div className="font-medium">{v}</div></div>;
}
